import { uiText } from '@/i18n/interface';
import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { toast } from "@/hooks/use-toast";
import { useAuth } from '../contexts/AuthContext';

interface NotificationData {
  type: 'training_session' | 'promotion_session' | 'announcement';
  title: string;
  message: string;
  dojoId: string;
  sessionData?: {
    date: string;
    start_time: string;
    end_time: string;
    description?: string;
  };
  ageFilter?: 'all' | 'kids' | 'teens' | 'adults';
}

interface StudentNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  session_id?: string;
  promotion_session_id?: string;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<StudentNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNotificationPopup, setNewNotificationPopup] = useState<StudentNotification | null>(null);
  const { user, profile } = useAuth();

  const fetchNotifications = async () => {
    if (!user || !profile) return;

    setLoading(true);
    try {
      console.log('Fetching notifications for user:', user.id);
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
        throw error;
      }

      console.log('Fetched notifications:', data);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to load notifications."),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user, profile]);

  // Set up real-time subscription for new notifications
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Real-time notification received:', payload);
          const newNotification = payload.new as StudentNotification;
          setNotifications(prev => [newNotification, ...prev]);

          // Show toast for new notification
          toast({
            title: uiText("New Notification"),
            description: newNotification.title,
          });

          // Show popup modal for new notification
          setNewNotificationPopup(newNotification);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const updatedNotification = payload.new as StudentNotification;
          setNotifications(prev =>
            prev.map(notification =>
              notification.id === updatedNotification.id
                ? updatedNotification
                : notification
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state immediately for better UX
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );

    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to mark notification as read."),
        variant: "destructive"
      });
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;

      // Update local state immediately
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );

    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to mark all notifications as read."),
        variant: "destructive"
      });
    }
  };

  const sendNotification = async (data: NotificationData) => {
    try {
      const { data: response, error } = await supabase.functions.invoke(
        'send-student-notifications',
        {
          body: data
        }
      );

      if (error) {
        console.error('Error sending notification:', error);
        toast({
          title: uiText("Notification Error"),
          description: uiText("Failed to send notifications to students."),
          variant: "destructive"
        });
        return false;
      }

      console.log('Notification sent successfully:', response);
      toast({
        title: uiText("Notifications Sent"),
        description: uiText("Successfully sent notifications to students.")
      });
      return true;
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: uiText("Notification Error"),
        description: uiText("Failed to send notifications to students."),
        variant: "destructive"
      });
      return false;
    }
  };

  const closeNotificationPopup = () => {
    setNewNotificationPopup(null);
  };

  return {
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    sendNotification,
    newNotificationPopup,
    closeNotificationPopup
  };
};
